import {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  useCallback,
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronDown20Regular } from "@fluentui/react-icons";

export type SearchableSelectValue = string | number;

export interface SearchableSelectOption<
  TValue extends SearchableSelectValue = SearchableSelectValue,
> {
  value: TValue;
  label: string;
  description?: string;
  keywords?: string;
  disabled?: boolean;
}

export interface SearchableSelectProps<
  TValue extends SearchableSelectValue = SearchableSelectValue,
> {
  options?: readonly SearchableSelectOption<TValue>[];
  value: TValue | null | undefined;
  onChange: (
    value: TValue | null,
    option: SearchableSelectOption<TValue> | null,
  ) => void;
  onSearch?: (
    term: string,
    signal: AbortSignal,
  ) => Promise<readonly SearchableSelectOption<TValue>[]>;
  label?: ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  required?: boolean;
  disabled?: boolean;
  /** Mensagem de erro do campo: marca-o inválido e liga-a por `aria-describedby`. */
  error?: string;
  loading?: boolean;
  allowClear?: boolean;
  autoFocus?: boolean;
  id?: string;
  ariaLabel?: string;
  className?: string;
  style?: CSSProperties;
  labelClassName?: string;
  labelStyle?: CSSProperties;
  /**
   * "fixed" tira o painel do fluxo e posiciona-o em coordenadas de viewport.
   * É o que é preciso dentro das tabelas com `overflow-x-auto`: aí um painel
   * absoluto fica cortado pela caixa que faz scroll.
   */
  panelPosition?: "absolute" | "fixed";
  maxVisible?: number;
  minSearchLength?: number;
  debounceMs?: number;
  serverPageSize?: number;
  renderOption?: (
    option: SearchableSelectOption<TValue>,
    state: { active: boolean; selected: boolean },
  ) => ReactNode;
}

const NORMALIZE_CACHE_LIMIT = 20000;
const normalizeCache = new Map<string, string>();

/** Minúsculas sem acentos, memoizado — a mesma etiqueta reaparece a cada refetch da lista. */
export function normalizeSearchText(value: string): string {
  const cached = normalizeCache.get(value);
  if (cached !== undefined) return cached;

  const normalized = value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  if (normalizeCache.size >= NORMALIZE_CACHE_LIMIT) normalizeCache.clear();
  normalizeCache.set(value, normalized);
  return normalized;
}

interface IndexedOption<TValue extends SearchableSelectValue> {
  option: SearchableSelectOption<TValue>;
  label: string;
  haystack: string;
}

function buildIndex<TValue extends SearchableSelectValue>(
  options: readonly SearchableSelectOption<TValue>[],
): IndexedOption<TValue>[] {
  return options.map((option) => {
    const label = normalizeSearchText(option.label);
    const extras: string[] = [label];
    if (option.description) extras.push(normalizeSearchText(option.description));
    if (option.keywords) extras.push(normalizeSearchText(option.keywords));
    return { option, label, haystack: extras.join(" ") };
  });
}

interface FilterResult<TValue extends SearchableSelectValue> {
  matches: SearchableSelectOption<TValue>[];
  total: number;
}

/**
 * Uma passagem sobre o índice já normalizado, com os resultados repartidos por três
 * baldes de relevância (prefixo, início de palavra, resto). Cada balde pára nos
 * `limit` — o custo que conta num seletor grande é o DOM, não o filtro.
 */
function filterIndex<TValue extends SearchableSelectValue>(
  index: readonly IndexedOption<TValue>[],
  tokens: readonly string[],
  limit: number,
): FilterResult<TValue> {
  if (tokens.length === 0) {
    return {
      matches: index.slice(0, limit).map((entry) => entry.option),
      total: index.length,
    };
  }

  const byPrefix: SearchableSelectOption<TValue>[] = [];
  const byWord: SearchableSelectOption<TValue>[] = [];
  const byRest: SearchableSelectOption<TValue>[] = [];
  const [first] = tokens;
  let total = 0;

  for (let i = 0; i < index.length; i += 1) {
    const entry = index[i];

    let matchesAll = true;
    for (let t = 0; t < tokens.length; t += 1) {
      if (entry.haystack.indexOf(tokens[t]) === -1) {
        matchesAll = false;
        break;
      }
    }
    if (!matchesAll) continue;

    total += 1;

    if (entry.label.startsWith(first)) {
      if (byPrefix.length < limit) byPrefix.push(entry.option);
    } else if (entry.label.indexOf(` ${first}`) !== -1) {
      if (byWord.length < limit) byWord.push(entry.option);
    } else if (byRest.length < limit) {
      byRest.push(entry.option);
    }
  }

  return {
    matches: byPrefix.concat(byWord, byRest).slice(0, limit),
    total,
  };
}

function tokenize(query: string): string[] {
  const normalized = normalizeSearchText(query).trim();
  if (normalized === "") return [];
  return normalized.split(/\s+/);
}

const PANEL_MAX_HEIGHT = 256;
const PANEL_GAP = 4;

interface AnchorRect {
  left: number;
  width: number;
  maxWidth: number;
  top?: number;
  bottom?: number;
}

function measureAnchor(element: HTMLElement): AnchorRect {
  const rect = element.getBoundingClientRect();
  const below = window.innerHeight - rect.bottom;
  // O painel pode ficar mais largo do que o botão para se lerem opções longas (ex.: nomes de
  // obra), mas nunca passa a borda direita do ecrã.
  const maxWidth = window.innerWidth - rect.left - PANEL_GAP;

  // Sem espaço em baixo, o painel abre para cima em vez de sair do ecrã.
  if (below < PANEL_MAX_HEIGHT && rect.top > below) {
    return {
      left: rect.left,
      width: rect.width,
      maxWidth,
      bottom: window.innerHeight - rect.top + PANEL_GAP,
    };
  }

  return { left: rect.left, width: rect.width, maxWidth, top: rect.bottom + PANEL_GAP };
}

interface RemoteEntry<TValue extends SearchableSelectValue> {
  options: readonly SearchableSelectOption<TValue>[];
  /** `false` quando o servidor cortou no topo: há mais resultados que não vieram. */
  complete: boolean;
}

export function SearchableSelect<
  TValue extends SearchableSelectValue = SearchableSelectValue,
>({
  options,
  value,
  onChange,
  onSearch,
  label,
  placeholder = "Selecione...",
  searchPlaceholder,
  emptyMessage = "Sem resultados",
  loadingMessage = "A procurar...",
  required = false,
  disabled = false,
  error,
  loading = false,
  allowClear = true,
  autoFocus = false,
  id,
  ariaLabel,
  className,
  style,
  labelClassName = "block text-foreground",
  labelStyle,
  panelPosition = "absolute",
  maxVisible = 100,
  minSearchLength,
  debounceMs = 250,
  serverPageSize = 50,
  renderOption,
}: SearchableSelectProps<TValue>) {
  const generatedId = useId();
  const inputId = id ?? `searchable-select-${generatedId}`;
  const listboxId = `${inputId}-listbox`;
  const errorId = `${inputId}-error`;
  const statusId = `${inputId}-status`;

  const isRemote = typeof onSearch === "function";
  const minLength = minSearchLength ?? (isRemote ? 2 : 0);

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [remote, setRemote] = useState<readonly SearchableSelectOption<TValue>[]>(
    [],
  );
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteTruncated, setRemoteTruncated] = useState(false);
  const [remoteError, setRemoteError] = useState<string | null>(null);
  const [lastSelected, setLastSelected] =
    useState<SearchableSelectOption<TValue> | null>(null);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);
  const remoteCache = useRef(new Map<string, RemoteEntry<TValue>>());
  // Guardado numa ref para sair das dependências do efeito: um `onSearch` escrito
  // em linha muda de identidade a cada render e re-armava o debounce sem fim.
  const onSearchRef = useRef(onSearch);
  const activeSearchRef = useRef<AbortController | null>(null);

  useEffect(() => {
    onSearchRef.current = onSearch;
  });

  const deferredQuery = useDeferredValue(query);

  const localIndex = useMemo(
    () => buildIndex(options ?? []),
    [options],
  );

  const remoteIndex = useMemo(() => buildIndex(remote), [remote]);

  const selectedOption = useMemo(() => {
    if (value === null || value === undefined) return null;
    const fromOptions = options?.find((option) => option.value === value);
    if (fromOptions) return fromOptions;
    if (lastSelected && lastSelected.value === value) return lastSelected;
    const fromRemote = remote.find((option) => option.value === value);
    return fromRemote ?? null;
  }, [options, remote, value, lastSelected]);

  const tokens = useMemo(() => tokenize(deferredQuery), [deferredQuery]);

  const { matches, total } = useMemo(() => {
    if (!isOpen) return { matches: [], total: 0 };
    // No modo servidor a filtragem já veio feita; aqui só se ordena/limita o que chegou.
    const index = isRemote ? remoteIndex : localIndex;
    return filterIndex(index, isRemote ? [] : tokens, maxVisible);
  }, [isOpen, isRemote, remoteIndex, localIndex, tokens, maxVisible]);

  const trimmedQuery = deferredQuery.trim();
  const belowMinLength = isRemote && trimmedQuery.length < minLength;

  useEffect(() => {
    if (!isRemote || !isOpen) return;

    if (trimmedQuery.length < minLength) {
      setRemote([]);
      setRemoteError(null);
      setRemoteLoading(false);
      setRemoteTruncated(false);
      return;
    }

    const term = normalizeSearchText(trimmedQuery);
    const cache = remoteCache.current;

    const exact = cache.get(term);
    if (exact) {
      setRemote(exact.options);
      setRemoteError(null);
      setRemoteLoading(false);
      setRemoteTruncated(!exact.complete);
      return;
    }

    // Se um prefixo já trouxe o conjunto completo, estreitar em memória em vez de
    // voltar ao servidor — escrever mais letras nunca alarga o resultado.
    //
    // Só com um termo escrito. O servidor procura o texto literal e contíguo
    // (`%termo%`), e o filtro local casa cada palavra em separado e sem ordem: com
    // dois termos os dois conjuntos divergem, e o painel mostrava linhas que uma
    // pesquisa feita de raiz nunca devolveria. Vale mais um pedido a mais.
    const singleTerm = tokenize(term).length === 1;
    for (let cut = term.length - 1; singleTerm && cut >= minLength; cut -= 1) {
      const entry = cache.get(term.slice(0, cut));
      if (!entry || !entry.complete) continue;
      const narrowed = filterIndex(
        buildIndex(entry.options),
        tokenize(term),
        serverPageSize,
      ).matches;
      cache.set(term, { options: narrowed, complete: true });
      setRemote(narrowed);
      setRemoteError(null);
      setRemoteLoading(false);
      setRemoteTruncated(false);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      activeSearchRef.current = controller;
      setRemoteLoading(true);
      onSearchRef.current!(trimmedQuery, controller.signal)
        .then((result) => {
          if (controller.signal.aborted) return;
          const complete = result.length < serverPageSize;
          cache.set(term, { options: result, complete });
          setRemote(result);
          setRemoteError(null);
          setRemoteTruncated(!complete);
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          setRemoteError(
            error instanceof Error ? error.message : "Falha na pesquisa",
          );
          setRemote([]);
          setRemoteTruncated(false);
        })
        .finally(() => {
          // Só o pedido que ainda é o actual desliga o indicador: um pedido antigo
          // a terminar depois de um novo arrancar deixava "A procurar…" a piscar.
          if (activeSearchRef.current !== controller) return;
          activeSearchRef.current = null;
          setRemoteLoading(false);
        });
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
      // Fechar o painel a meio de um pedido deixava o indicador preso a "A procurar…"
      // para sempre em quem usasse minSearchLength={0}.
      if (activeSearchRef.current === controller) {
        activeSearchRef.current = null;
        setRemoteLoading(false);
      }
    };
  }, [isRemote, isOpen, trimmedQuery, minLength, debounceMs, serverPageSize]);

  useEffect(() => {
    if (!isOpen) return;
    activeRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [highlight, isOpen, matches.length]);

  useEffect(() => {
    if (panelPosition !== "fixed" || !isOpen) {
      setAnchor(null);
      return;
    }

    const track = () => {
      if (fieldRef.current) setAnchor(measureAnchor(fieldRef.current));
    };

    track();
    // `true` apanha também o scroll das tabelas: o painel acompanha o campo em vez
    // de ficar pendurado onde estava.
    window.addEventListener("scroll", track, true);
    window.addEventListener("resize", track);

    return () => {
      window.removeEventListener("scroll", track, true);
      window.removeEventListener("resize", track);
    };
  }, [panelPosition, isOpen]);

  const activeIndex = matches.length > 0 ? Math.min(highlight, matches.length - 1) : -1;

  const open = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setQuery("");
    // Reabrir com um valor escolhido arranca nesse valor, como faz o <select>
    // nativo. A começar sempre em 0, quem reabria a meio de uma lista longa
    // perdia o sítio e o `aria-activedescendant` apontava à primeira linha.
    // Com a caixa de pesquisa vazia as linhas desenhadas são a cabeça da lista pela
    // mesma ordem, por isso o índice serve tal e qual — mas só até `maxVisible`:
    // além disso a opção nem chega a ser desenhada e não há nada para realçar.
    const selectedIndex =
      value === null || value === undefined
        ? -1
        : (options ?? []).findIndex((option) => option.value === value);
    setHighlight(
      selectedIndex > 0 && selectedIndex < maxVisible ? selectedIndex : 0,
    );
  }, [disabled, options, value, maxVisible]);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setRemoteError(null);
  }, []);

  const commit = useCallback(
    (option: SearchableSelectOption<TValue> | null) => {
      if (option?.disabled) return;
      setLastSelected(option);
      onChange(option ? option.value : null, option);
      close();
    },
    [onChange, close],
  );

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      if (isOpen) {
        event.preventDefault();
        // Travar aqui a propagação: sem isto o Escape continua até aos handlers de
        // documento que fecham modais e painéis, e fechar a lista descartava o
        // formulário por trás dela.
        event.stopPropagation();
        close();
      }
      return;
    }

    if (!isOpen) {
      if (
        event.key === "ArrowDown" ||
        event.key === "ArrowUp" ||
        event.key === "Enter"
      ) {
        event.preventDefault();
        open();
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (matches.length > 0) {
          setHighlight((activeIndex + 1) % matches.length);
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (matches.length > 0) {
          setHighlight((activeIndex - 1 + matches.length) % matches.length);
        }
        break;
      case "Home":
        event.preventDefault();
        setHighlight(0);
        break;
      case "End":
        event.preventDefault();
        setHighlight(Math.max(matches.length - 1, 0));
        break;
      case "Enter":
        event.preventDefault();
        if (activeIndex >= 0) commit(matches[activeIndex]);
        break;
      case "Tab":
        if (activeIndex >= 0 && query.trim() !== "") commit(matches[activeIndex]);
        break;
      default:
        break;
    }
  };

  const showLoading = loading || remoteLoading;

  // Com valor mas sem opção que lhe corresponda — a lista ainda vem a caminho, ou o
  // registo foi desactivado — o campo mostrava-se vazio e quem lá estava assumia que
  // não tinha escolhido nada. Vale mais dizer que há algo escolhido que não se soube ler.
  const hasValue = value !== null && value !== undefined && value !== "";
  const resolvedLabel = selectedOption
    ? selectedOption.label
    : hasValue
      ? showLoading
        ? loadingMessage
        : `#${value}`
      : "";

  const showClear =
    allowClear && !disabled && value !== null && value !== undefined && value !== "";
  // Uma única frase para o que está a acontecer, usada no painel e anunciada.
  const statusMessage = !isOpen
    ? ""
    : belowMinLength
      ? `Escreva pelo menos ${minLength} caracteres para procurar.`
      : showLoading && matches.length === 0
        ? loadingMessage
        : remoteError
          ? remoteError
          : matches.length === 0
            ? emptyMessage
            : total > matches.length
              ? `${matches.length} de ${total} resultados. Refine a pesquisa.`
              : remoteTruncated
                ? `${matches.length} resultados, há mais do que os mostrados. Refine a pesquisa.`
                : `${matches.length} ${matches.length === 1 ? "resultado" : "resultados"}.`;

  const inputValue = isOpen ? query : resolvedLabel;
  const inputPlaceholder = isOpen
    ? resolvedLabel || searchPlaceholder || "Escreva para procurar..."
    : placeholder;

  return (
    <div className={`relative flex flex-col gap-1 ${className ?? ""}`} style={style}>
      {label !== undefined && (
        <label
          htmlFor={inputId}
          className={labelClassName}
          style={
            labelStyle ?? {
              fontSize: "var(--text-caption)",
              fontWeight: "var(--font-weight-semi-bold)",
            }
          }
        >
          {label}
          {required && <span style={{ color: "var(--destructive)" }}> *</span>}
        </label>
      )}

      <div className="relative" ref={fieldRef}>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          autoFocus={autoFocus}
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          aria-required={required ? "true" : undefined}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={error ? errorId : undefined}
          aria-activedescendant={
            isOpen && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
          }
          disabled={disabled}
          value={inputValue}
          placeholder={inputPlaceholder}
          onChange={(event) => {
            if (!isOpen) setIsOpen(true);
            setQuery(event.target.value);
            setHighlight(0);
          }}
          onFocus={() => {
            if (!isOpen) open();
          }}
          // O painel cancela o mousedown para não roubar o foco, por isso depois de
          // escolher o input continua focado e um segundo clique não dispara focus.
          // Sem isto, o campo ficava morto até se clicar noutro sítio.
          onMouseDown={() => {
            if (!isOpen && document.activeElement === inputRef.current) open();
          }}
          // O blur cobre Tab para fora e cliques fora; os cliques no painel não o
          // disparam porque o mousedown lá em baixo é cancelado.
          onBlur={close}
          onKeyDown={handleKeyDown}
          className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            borderRadius: "var(--radius-button)",
            fontSize: "var(--text-base)",
            // Espaço para a seta, e para o × quando há algo escolhido.
            paddingRight: showClear ? "3.75rem" : "2.25rem",
            // Fechado é um gatilho e pede mão; aberto está-se a escrever.
            cursor: disabled ? "not-allowed" : isOpen ? "text" : "pointer",
          }}
        />

        {showClear && (
          <button
            type="button"
            aria-label="Limpar seleção"
            className="absolute top-0 h-full px-2 text-secondary-text hover:text-foreground cursor-pointer"
            style={{ right: "1.75rem" }}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => commit(null)}
          >
            ×
          </button>
        )}

        {/* Decorativa: sem eventos, o clique atravessa-a e cai no input, que é quem
            abre a lista — assim a seta nunca fica a ser um alvo morto. */}
        <ChevronDown20Regular
          aria-hidden="true"
          className="w-5 h-5 text-secondary-text absolute pointer-events-none transition-transform"
          // O centrar e a rotação vivem os dois no `transform`, e as classes
          // `top-1/2`/`-translate-y-1/2` ficam de fora de propósito: no Tailwind v4
          // `-translate-y-1/2` compila para a propriedade `translate:`, que não é
          // substituída por um `transform:` inline — soma-se a ele, e a seta subia
          // o dobro do que devia.
          style={{
            right: "0.5rem",
            top: "50%",
            transform: isOpen
              ? "translateY(-50%) rotate(180deg)"
              : "translateY(-50%)",
            opacity: disabled ? 0.5 : 1,
          }}
        />
      </div>

      {isOpen && (
        <div
          ref={listRef}
          id={listboxId}
          role="listbox"
          className="bg-card border border-border shadow-lg overflow-y-auto"
          style={
            panelPosition === "fixed"
              ? {
                  position: "fixed",
                  zIndex: 70,
                  left: anchor?.left ?? 0,
                  // Cresce para caber o texto (max-content), nunca mais estreito que o botão, nunca
                  // além da borda do ecrã.
                  width: "max-content",
                  minWidth: anchor?.width ?? "auto",
                  maxWidth: anchor?.maxWidth,
                  top: anchor?.top,
                  bottom: anchor?.bottom,
                  visibility: anchor ? "visible" : "hidden",
                  maxHeight: `${PANEL_MAX_HEIGHT}px`,
                  borderRadius: "var(--radius-button)",
                }
              : {
                  position: "absolute",
                  zIndex: 50,
                  left: 0,
                  width: "max-content",
                  minWidth: "100%",
                  maxWidth: "min(28rem, 90vw)",
                  top: "100%",
                  marginTop: `${PANEL_GAP}px`,
                  maxHeight: `${PANEL_MAX_HEIGHT}px`,
                  borderRadius: "var(--radius-button)",
                }
          }
          // mousedown cancelado para o input não perder o foco (o blur fecharia o
          // painel antes do click acertar — inclui a barra de scroll).
          onMouseDown={(event) => event.preventDefault()}
        >
          {belowMinLength ? (
            <div
              className="px-3 py-2 text-secondary-text"
              style={{ fontSize: "var(--text-caption)" }}
            >
              Escreva pelo menos {minLength} caracteres para procurar.
            </div>
          ) : showLoading && matches.length === 0 ? (
            <div
              className="px-3 py-2 text-secondary-text"
              style={{ fontSize: "var(--text-caption)" }}
            >
              {loadingMessage}
            </div>
          ) : remoteError ? (
            <div
              className="px-3 py-2"
              style={{
                fontSize: "var(--text-caption)",
                color: "var(--destructive)",
              }}
            >
              {remoteError}
            </div>
          ) : matches.length === 0 ? (
            <div
              className="px-3 py-2 text-secondary-text"
              style={{ fontSize: "var(--text-caption)" }}
            >
              {emptyMessage}
            </div>
          ) : (
            <>
              {matches.map((option, index) => {
                const active = index === activeIndex;
                const selected = selectedOption?.value === option.value;
                return (
                  <div
                    key={String(option.value)}
                    id={`${listboxId}-option-${index}`}
                    ref={active ? activeRef : undefined}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={option.disabled ? "true" : undefined}
                    className={`px-3 py-2 text-left transition-colors ${
                      option.disabled
                        ? "opacity-50 cursor-not-allowed"
                        : `cursor-pointer ${active ? "bg-accent" : "hover:bg-accent"}`
                    } ${selected ? "font-medium" : ""}`}
                    // O `bg-accent` sozinho tem ~1,1:1 de contraste com o fundo do
                    // painel — invisível para quem tem baixa visão. A barra à esquerda
                    // é o sinal que não depende de cor de fundo. (`ring-*` não existe
                    // no CSS pré-compilado deste repo, por isso vai em style.)
                    style={
                      active
                        ? { boxShadow: "inset 3px 0 0 0 var(--primary)" }
                        : undefined
                    }
                    onMouseEnter={() => !option.disabled && setHighlight(index)}
                    onClick={() => commit(option)}
                  >
                    {renderOption ? (
                      renderOption(option, { active, selected })
                    ) : (
                      <>
                        <div className="truncate">
                          {selected && (
                            <span aria-hidden="true" style={{ marginRight: "0.375rem" }}>
                              ✓
                            </span>
                          )}
                          {option.label}
                        </div>
                        {option.description && (
                          <div
                            className="truncate text-secondary-text"
                            style={{ fontSize: "var(--text-caption)" }}
                          >
                            {option.description}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
              {total > matches.length ? (
                <div
                  className="px-3 py-2 text-secondary-text border-t border-border"
                  style={{ fontSize: "var(--text-caption)" }}
                >
                  Mais {total - matches.length} resultados — refine a pesquisa.
                </div>
              ) : (
                remoteTruncated && (
                  <div
                    className="px-3 py-2 text-secondary-text border-t border-border"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Há mais resultados do que os mostrados — refine a pesquisa.
                  </div>
                )
              )}
            </>
          )}
        </div>
      )}

      {/* Anúncio do que mudou na lista. Fica sempre montado — uma live region só
          anuncia se já existir no DOM quando o texto muda. Escondida com estilo e
          não com `sr-only`, que não existe no CSS pré-compilado deste repo. */}
      <div
        id={statusId}
        role="status"
        aria-live="polite"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {statusMessage}
      </div>

      {error && (
        <p
          id={errorId}
          style={{
            fontSize: "var(--text-caption)",
            color: "var(--destructive)",
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

export default SearchableSelect;
